import { Heading, Icon, IconName, Tag, Text } from '@zapier/design-system';
import React from 'react';
import styles from './PageHeader.module.scss';
import { PageHeaderBase, PageHeaderBaseProps } from './PageHeaderBase';

export type PageHeaderProps = {
  /** Optional breadcrumbs to display at the top of the header.
   * You should use the `small` size for breadcrumbs.
   */
  breadcrumbs?: PageHeaderBaseProps['topRowContent'];
  /** The main page title (H1) */
  title: React.ReactNode;
  /** Optional icon to display to the left of the title.
   * Pass in an IconName to use a ZinniaIcon, or pass in a custom ReactNode.
   */
  pageIcon?: IconName | Exclude<React.ReactNode, string>;
  /** Optional tag to display to the right of the title.
   * Pass in a string to use a default `Tag` component with a `simple` variant,
   * or provide custom content.
   */
  pageTag?: React.ReactNode;
  /** Actions to show at larger screen sizes */
  actions?: React.ReactNode;
  /** Actions to show at smaller screen sizes (e.g. IconButtons instead of Buttons)
   * If compactActions aren't provided, the actions will show.
   */
  compactActions?: React.ReactNode;
  /** Optional description text below the title */
  description?: React.ReactNode;
  /** Optional actions/content below the description.
   * Use cases include Tabs or search/filter functionality.
   */
  additionalContent?: React.ReactNode;
  /** Optional color for the page icon. Defaults to "inherit" */
  pageIconColor?: string;
};

/**
 * A consistent page header layout component that includes support for:
 * - Breadcrumbs navigation
 * - Page title with (optional) actions
 * - Optional page description
 * - Optional additional content area.
 *
 * Based on the `PageHeaderBase` component, `PageHeader` is more opinionated.
 * Reach for the `PageHeaderBase` component when you want to customize the header
 * content, but keep the overall layout.
 */
export const PageHeader = ({
  breadcrumbs,
  title,
  actions,
  compactActions,
  description,
  additionalContent,
  pageIcon,
  pageTag,
  pageIconColor = 'inherit',
}: PageHeaderProps) => {
  return (
    <PageHeaderBase topRowContent={breadcrumbs}>
      <header className={styles.headerWrapper} data-zds>
        <div className={styles.titleRow}>
          <div className={styles.titleContent}>
            {pageIcon && (
              <div
                className={styles.pageIcon}
                style={{ fill: pageIconColor }}
                data-zds
              >
                {typeof pageIcon === 'string' ? (
                  <Icon
                    color="inherit"
                    size="var(--page-icon-size)"
                    name={pageIcon as IconName}
                  />
                ) : (
                  pageIcon
                )}
              </div>
            )}
            <Heading className={styles.title}>{title}</Heading>
            {pageTag && (
              <div className={styles.pageTag} data-zds>
                {typeof pageTag === 'string' ? (
                  <Tag
                    variant="simple"
                    size="x-small"
                    className={styles.responsiveTag}
                  >
                    {pageTag}
                  </Tag>
                ) : (
                  pageTag
                )}
              </div>
            )}
          </div>
          {(actions ?? compactActions) && (
            <div className={styles.actionsContainer} data-zds>
              {compactActions && (
                <div className={styles.compactActions}>{compactActions}</div>
              )}
              {actions && (
                <div className={styles.defaultActions}>{actions}</div>
              )}
            </div>
          )}
        </div>
        {description && (
          <Text
            tag="p"
            type="Body1Multiline"
            color="GrayWarm9"
            style={{ padding: 'var(--zds-space-8) 0' }}
          >
            {description}
          </Text>
        )}
      </header>
      {additionalContent && (
        <div className={styles.additionalContent} data-zds>
          {additionalContent}
        </div>
      )}
    </PageHeaderBase>
  );
};
